import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import TestResultsDisplay from '../components/TestResultsDisplay';
import type { Test } from '../types';

const KioskResults: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (!testId) {
        setIsLoading(false);
        return;
      }

      const storedSession = sessionStorage.getItem('kioskSession');
      const kioskSession = storedSession ? JSON.parse(storedSession) : null;

      if ((!kioskSession?.companyCode && !kioskSession?.companyId) || !kioskSession?.employeeId) {
        setError('Kiosk session not found. Please start a new test.');
        setIsLoading(false);
        return;
      }

      const response = await apiService.getKioskTestResults(testId, {
        companyCode: kioskSession.companyCode,
        companyId: kioskSession.companyId,
        employeeId: kioskSession.employeeId
      });

      if (response.success && response.data) {
        setTest(response.data as Test);
      } else {
        setError(response.error?.message || 'Results not available yet.');
      }
      setIsLoading(false);
    };

    fetchResults();
  }, [testId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="text-navy-200">Loading...</div>
      </div>
    );
  }

  if (error || !test || !test.results) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="text-center text-navy-200">
          <p className="mb-4">{error || 'Results not found.'}</p>
          <Link to="/kiosk/start" className="text-gold-400 underline">
            Start a new test
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800 p-8">
      <div className="max-w-5xl mx-auto">
        <TestResultsDisplay test={test as Test & { user: any }} showUserInfo={false} showTestInfo={true} />

        <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6 print:hidden">
          <Link
            to="/kiosk/start"
            className="bg-gold-500 hover:bg-gold-600 text-navy-950 px-6 py-3 rounded-lg font-medium text-center"
          >
            Start Another Test
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KioskResults;
